import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Field,
  FormActions,
  Input,
  Spinner,
  Textarea,
} from '@/components/ui';
import { useFormDraft } from '@/features/drafts';
import { QueueStorageFullError, enqueue } from '@/features/upload-queue';
import { CameraError, capturePhoto, captureVideo, NORMAL_PRESET } from '@/native';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { useMobileSite } from '@/hooks/useCurrentSite';
import { siteDateKey } from '@/lib/site-time';
import { useAuthStore } from '@/store/auth';

/** 被系统打断后要回到的地址。`compose=1` 让这个弹层自己重新打开（CR-037） */
export const COMPOSE_ROUTE = '/m/logs?compose=1';

/**
 * 写今日日报（CR-023 重建）。
 *
 * **这个弹层原来的唯一入参是 `task`**，唯一入口在「我的任务」页里。
 * 任务模块删掉之后工人端就没有任何路径能写日报了，所以它整个重写：
 * 工地从 `useMobileSite()` 取（在班锁定跟着走），日期默认工地当地今天，
 * 没有提交要求、没有自定义字段、没有审核 —— 分包写一段话，配图或视频。
 *
 * 提交照旧走上传队列：工地没信号时先落盘，联网后自动补传。
 */
export function SubmitLogSheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const toMessage = useErrorMessage();

  // 采购是唯一豁免锁定的模块（约束 10），日报不是 —— 在班就写这个工地的日报
  const { site, siteId } = useMobileSite();
  const timezone = site?.timezone ?? 'America/Los_Angeles';

  /**
   * 填到一半的内容落盘（CR-037）。
   *
   * 拍照会让安卓回收这个 Activity，回来时 WebView 是重新加载的 ——
   * 用 `useState` 存的日期和正文一个字都不会剩。
   */
  const draft = useFormDraft(
    'daily-log',
    user?.id,
    { workDate: siteDateKey(timezone), content: '' },
    COMPOSE_ROUTE,
  );
  const { workDate, content } = draft.fields;

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'photo' | 'video' | 'submit' | null>(null);

  async function addPhoto() {
    setError(null);
    setBusy('photo');
    try {
      const photo = await capturePhoto(NORMAL_PRESET, draft.captureSession('normal'));
      await draft.addShot({
        blob: photo.blob,
        mimeType: photo.mimeType,
        capturedAt: photo.capturedAt,
        isVideo: false,
      });
    } catch (err) {
      if (!(err instanceof CameraError && err.kind === 'CANCELLED')) {
        setError(t('hidden.cameraFailed'));
      }
    } finally {
      setBusy(null);
    }
  }

  async function addVideo() {
    setError(null);
    setBusy('video');
    try {
      const video = await captureVideo(draft.captureSession('normal'));
      await draft.addShot({
        blob: video.blob,
        mimeType: video.mimeType,
        capturedAt: video.capturedAt,
        isVideo: true,
      });
    } catch (err) {
      if (err instanceof CameraError && err.kind === 'TOO_LARGE') {
        setError(t('logs.videoTooLarge'));
      } else if (!(err instanceof CameraError && err.kind === 'CANCELLED')) {
        setError(t('hidden.cameraFailed'));
      }
    } finally {
      setBusy(null);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!user || siteId === null) return;
    if (!content.trim() && draft.shots.length === 0) {
      setError(t('logs.emptyError'));
      return;
    }

    setBusy('submit');
    try {
      await enqueue({
        kind: 'DAILY_LOG',
        userId: user.id,
        siteId,
        payload: { workDate, content: content.trim() || undefined },
        files: draft.shots.map((shot) => ({ blob: shot.blob, mimeType: shot.mimeType })),
      });

      // 进了队列就不再是草稿了 —— 传上去这件事从这一刻起由队列负责
      await draft.clear();
      // 列表里那条「上传中」由队列快照渲染，传完之后 App.tsx 会让列表失效
      onClose();
    } catch (err) {
      setError(err instanceof QueueStorageFullError ? t('queue.storageFull') : toMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    /*
      手机上是底部弹层；电脑宽度（md，和 useMobileShell 同一个界限）改成居中的对话框（CR-041）。
      网页端「我的日报」复用这一页（CR-040），宽屏上铺满整行的底部弹层客户看着别扭。
      点背景关闭在两种形态下都保留：窄屏是上面那一截空白，宽屏是垫在对话框下面的整块。
    */
    <div
      className="fixed inset-0 z-toast flex flex-col bg-black/50 md:items-center md:justify-center md:p-6"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        className="flex-1 md:absolute md:inset-0"
        aria-label={t('app.cancel')}
        onClick={onClose}
      />

      <div className="safe-bottom max-h-[88vh] overflow-y-auto rounded-t-2xl bg-paper px-5 pb-6 pt-5 md:relative md:w-full md:max-w-lg md:rounded-2xl md:shadow-pop">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-body text-ink-3">{t('logs.submitLog')}</p>
            <h2 className="mt-0.5 truncate text-title font-semibold leading-snug">{site?.name}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-touch shrink-0 items-center rounded-md px-2 text-body text-ink-3 hover:bg-surface-2"
          >
            {t('app.close')}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && <Alert>{error}</Alert>}

          {draft.restored && <RestoredHint onDiscard={() => void draft.clear()} />}

          <Field label={t('logs.workDate')} htmlFor="log-date">
            <Input
              id="log-date"
              type="date"
              value={workDate}
              max={siteDateKey(timezone)}
              onChange={(e) => draft.setField('workDate', e.target.value)}
            />
          </Field>

          <Field label={t('logs.logContent')} htmlFor="log-content">
            <Textarea
              id="log-content"
              rows={5}
              value={content}
              onChange={(e) => draft.setField('content', e.target.value)}
              maxLength={4000}
              placeholder={t('logs.logContentPlaceholder')}
            />
          </Field>

          <div>
            <p className="mb-2 text-body font-medium text-ink-2">
              {t('logs.media')}
              <span className="ml-2 font-normal text-ink-3">
                {t('logs.mediaCount', { count: draft.shots.length })}
              </span>
              {/* 草稿里的媒体是从 IndexedDB 读回来的，慢的那几百毫秒要有个交代 */}
              {!draft.ready && <Spinner size={14} />}
            </p>

            {draft.shots.length > 0 && (
              <ul className="mb-3 grid grid-cols-3 gap-2">
                {draft.shots.map((shot) => (
                  <li key={shot.id} className="relative">
                    {shot.isVideo ? (
                      <video
                        src={shot.url}
                        preload="metadata"
                        className="aspect-square w-full rounded-md border border-rule bg-black object-cover"
                      />
                    ) : (
                      <img
                        src={shot.url}
                        alt=""
                        className="aspect-square w-full rounded-md border border-rule object-cover"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => void draft.removeShot(shot.id)}
                      aria-label={t('app.delete')}
                      className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-body text-white"
                    >
                      {t('app.delete')}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                block
                loading={busy === 'photo'}
                onClick={() => void addPhoto()}
              >
                {t('logs.addPhoto')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                block
                loading={busy === 'video'}
                onClick={() => void addVideo()}
              >
                {t('logs.addVideo')}
              </Button>
            </div>
          </div>

          {/* 左取消右提交（按钮位置规矩）。右上角的 × 照旧 */}
          <FormActions
            submitLabel={t('logs.submitLog')}
            cancelLabel={t('app.cancel')}
            onCancel={onClose}
            loading={busy === 'submit'}
            disabled={siteId === null}
          />
        </form>
      </div>
    </div>
  );
}

/**
 * 「这张表是上次没填完的」（CR-037）。
 *
 * 不说这一句，工人打开写日报看到里面已经有字，第一反应是系统串了别人的记录。
 * 旁边给一个「清空」—— 恢复必须是可以拒绝的，否则他要一个字一个字删。
 */
function RestoredHint({ onDiscard }: { onDiscard: () => void }) {
  const { t } = useTranslation();
  return (
    <p className="flex items-center justify-between gap-3 rounded-md bg-surface-2 px-3 py-2 text-caption text-ink-2">
      <span>{t('drafts.restored')}</span>
      <button type="button" onClick={onDiscard} className="shrink-0 underline">
        {t('drafts.discard')}
      </button>
    </p>
  );
}
